import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import {
  transformConfigurationResponseHandler,
  transformResponseHandler,
} from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const propertyApi = createApi({
  reducerPath: REDUCER_PATHS.PROPERTY,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    propertyListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_LISTING}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
    }),
    propertySingleDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_LISTING}?page=${data?.page}&pageSize=${data?.pageSize}&sortDirection=-1&slug=${data?.slug}&languageId=${data?.languageId}`,
        method: "GET",
      }),
    }),
    propertyGeneralOptions: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_GENERAL_OPTIONS}?filter=${data?.filter}`,
        method: "GET",
      }),
    }),
    propertySaleProceedOptions: builder.query({
      query: () => ({
        url: apiConfig.PROPERTY_SALE_PROCEED_OPTIONS,
        method: "GET",
      }),
    }),
    addProperty: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY_ADD,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editProperty: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_EDIT}/${data?.id}`,
        body: data?.postData,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteProperty: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY_DELETE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPropertyDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.GET_PROPERTY_DETAILS}/${data.propertyId}`,
        method: "GET",
      }),
    }),
    statusChangeProperty: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY}/${data.propertyId}?slug=${data.slug}`,
        method: "GET",
      }),
    }),
    propertyDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();
        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);
        return {
          url: `${apiConfig.PROPERTY_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
      invalidatesTags: ["propertyList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyIndividualUserDetails: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_DOWNLOAD}/${data}`,
          method: "GET",
        };
      },
      providesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    changePropertyStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY}?userCode=${data?.userCode}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["userDetails"],
    }),
    //gallery api
    addPropertyImageGallery: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_IMAGE_GALLERY}`,
          body: data,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
      invalidatesTags: ["propertyImageGallery"],
    }),
    editPropertyImageGallery: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_IMAGE_GALLERY_EDIT}/${data?.galleryId}`,
          body: data?.data,
          method: "PUT",
        };
      },
      invalidatesTags: ["propertyImageGallery"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPropertyImageGallery: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.GET_PROPERTY_IMAGE_GALLERY}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyImageGallery"],
    }),
    addPropertyVideoGallery: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_VIDEO_GALLERY}`,
          body: data,
          method: "POST",
        };
      },
      providesTags: ["propertyVideoGallery"],
    }),
    addPropertyDocumentGallery: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.ADD_PROPERTY_DOCUMENT}`,
          body: data,
          method: "POST",
        };
      },
      providesTags: ["propertyDocumentGallery"],
    }),
    deletePropertyImageGallery: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.DELETE_PROPERTY_GALLERY}`,
          body: data,
          method: "DELETE",
        };
      },
      providesTags: ["propertyImageGallery"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    fetchPropertyGalleryData: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.GET_PROPERTY_IMAGE_GALLERY}/${data?.galleryId}`,
          method: "GET",
        };
      },
      providesTags: ["propertyImageGallery"],
    }),
    //cost constant api
    addPropertyCostConstant: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_COST_CONSTANTS}`,
          body: data,
          method: "POST",
        };
      },
      providesTags: ["propertyCostConstantsSchema"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editPropertyCostConstant: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.EDIT_PROPERTY_COST_CONSTANTS}/${data?.costConstantsId}`,
          body: data?.data,
          method: "PUT",
        };
      },
      providesTags: ["propertyCostConstantsSchema"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deletePropertyCostConstant: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.DELETE_PROPERTY_COST_CONSTANTS}`,
          method: "DELETE",
          body: data,
        };
      },
      providesTags: ["propertyCostConstantsSchema"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPropertyCostConstant: builder.query({
      query: (propertyId) => {
        return {
          url: `${apiConfig.GET_PROPERTY_COST_CONSTANTS}/${propertyId}`,
          method: "GET",
        };
      },
      providesTags: ["propertyCostConstantsSchema"],
    }),
    listPropertyCostConstant: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.GET_PROPERTY_COST_CONSTANTS}?propertyId=${data?.propertyId}&languageId=${data?.languageId}&slug=${data?.slug}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyCostConstantsSchema"],
    }),
    //valuation
    addPropertyValuation: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.ADD_PROPERTY_VALUATION}`,
          body: data,
          method: "POST",
        };
      },
      providesTags: ["propertyValuation"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    editPropertyValuation: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.EDIT_PROPERTY_VALUATION}/${data?.valuationId}`,
          body: data?.data,
          method: "PUT",
        };
      },
      providesTags: ["propertyValuation"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deletePropertyValuation: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_VALUATION}`,
          method: "DELETE",
          body: data,
        };
      },
      providesTags: ["propertyValuation"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPropertyValuation: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.GET_PROPERTY_VALUATION}/${data?.valuationId}`,
          method: "GET",
        };
      },
      providesTags: ["propertyValuation"],
    }),
    listPropertyValuation: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.LIST_PROPERTY_VALUATION}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyValuation"],
    }),
    deletePropertyValuation: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.DELETE_PROPERTY_VALUATION}`,
          method: "DELETE",
          body: data,
        };
      },
      providesTags: ["propertyValuation"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    //document
    addPropertyDocument: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_DOCUMENT}`,
          body: data,
          method: "POST",
        };
      },
      providesTags: ["propertyDocument"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    deletePropertyDocument: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.DELETE_PROPERTY_DOCUMENT}`,
          method: "DELETE",
          body: data,
        };
      },
      providesTags: ["propertyDocument"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editPropertyDocument: builder.mutation({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_DOCUMENT}/${data?.documentId}`,
          body: data?.data,
          method: "PUT",
        };
      },
      providesTags: ["propertyDocument"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPropertyDocumentData: builder.query({
      query: (documentId) => {
        return {
          url: `${apiConfig.GET_PROPERTY_DOCUMENT_LIST}/${documentId}`,
          method: "GET",
        };
      },
      providesTags: ["propertyDocument"],
    }),
    documentListing: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.GET_PROPERTY_DOCUMENT_LIST}?propertyId=${data?.propertyId}&languageId=${data?.languageId}&slug=${data?.slug}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyDocument"],
    }),
    //investment
    propertyInvestmentList: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_INVESTMENT_LIST}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyInvestment"],
    }),
    //saleProceed
    propertySaleProceedList: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_SALE_PROCEED_LIST}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertySaleProceed"],
    }),
    //dividend
    propertyDividendList: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.PROPERTY_DIVIDEND_LIST}?propertyId=${data?.propertyId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
          method: "GET",
        };
      },
      providesTags: ["propertyDividend"],
    }),
  }),
});

export const {
  useLazyPropertyListingQuery,
  usePropertySingleDetailsQuery,
  useLazyPropertySingleDetailsQuery,
  useLazyGetPropertyDetailsQuery,
  useAddPropertyMutation,
  useDeletePropertyMutation,
  useStatusChangePropertyMutation,
  usePropertyDownloadMutation,
  usePropertyDetailsQuery,
  useEditPropertyMutation,
  useAddPropertyImageGalleryMutation,
  useLazyGetPropertyImageGalleryQuery,
  useAddPropertyVideoGalleryMutation,
  useAddPropertyDocumentGalleryMutation,
  useDeletePropertyImageGalleryMutation,
  useEditPropertyImageGalleryMutation,
  useLazyFetchPropertyGalleryDataQuery,
  //cost constant api
  useAddPropertyCostConstantMutation,
  useDeletePropertyCostConstantMutation,
  useEditPropertyCostConstantMutation,
  useLazyGetPropertyCostConstantQuery,
  useLazyListPropertyCostConstantQuery,
  //valuation
  useAddPropertyValuationMutation,
  useEditPropertyValuationMutation,
  useLazyGetPropertyValuationQuery,
  useLazyListPropertyValuationQuery,
  useDeletePropertyValuationMutation,
  //document
  useAddPropertyDocumentMutation,
  useDeletePropertyDocumentMutation,
  useEditPropertyDocumentMutation,
  useLazyGetPropertyDocumentDataQuery,
  useLazyDocumentListingQuery,
  useLazyPropertyGeneralOptionsQuery,
  useLazyPropertySaleProceedOptionsQuery,
  useLazyPropertyInvestmentListQuery,
  useLazyPropertySaleProceedListQuery,
  useLazyPropertyDividendListQuery,
} = propertyApi;

export default propertyApi;
